/**
 * Calculation Utilities
 *
 * Business logic calculations for payments, tickets, and revenue.
 * Extracted from components for better testability and reusability.
 */

import { PaymentDetail } from "../types";

/**
 * Payment statistics structure
 */
export interface PaymentStats {
  total: number;
  successful: number;
  failed: number;
  pending: number;
  successRate: number;
  totalRevenue: number;
  averageTicketValue: number;
}

/**
 * Calculate payment statistics
 *
 * @param payments - Array of payment details
 * @returns Aggregated payment statistics
 *
 * @example
 * ```tsx
 * const stats = calculatePaymentStats(payments);
 * console.log(`Success rate: ${stats.successRate}%`);
 * ```
 */
export const calculatePaymentStats = (
  payments: PaymentDetail[]
): PaymentStats => {
  if (!payments.length) {
    return {
      total: 0,
      successful: 0,
      failed: 0,
      pending: 0,
      successRate: 0,
      totalRevenue: 0,
      averageTicketValue: 0,
    };
  }

  const successful = payments.filter(
    (p) => p.status.toLowerCase() === "success"
  ).length;
  const failed = payments.filter(
    (p) => p.status.toLowerCase() === "failed"
  ).length;
  const pending = payments.filter(
    (p) => p.status.toLowerCase() === "pending"
  ).length;

  const totalRevenue = payments
    .filter((p) => p.status.toLowerCase() === "success")
    .reduce((sum, p) => sum + p.amount, 0);

  const successRate = (successful / payments.length) * 100;
  const averageTicketValue = successful > 0 ? totalRevenue / successful : 0;

  return {
    total: payments.length,
    successful,
    failed,
    pending,
    successRate,
    totalRevenue,
    averageTicketValue,
  };
};

/**
 * Filter successful payments
 *
 * @param payments - Array of payment details
 * @returns Only successful payments
 *
 * @example
 * ```tsx
 * const successfulPayments = getSuccessfulPayments(allPayments);
 * ```
 */
export const getSuccessfulPayments = (
  payments: PaymentDetail[]
): PaymentDetail[] => {
  return payments.filter((p) => p.status.toLowerCase() === "success");
};

/**
 * Calculate total revenue from payments
 *
 * @param payments - Array of payment details
 * @param status - Optional filter by payment status
 * @returns Total revenue amount
 *
 * @example
 * ```tsx
 * const totalRevenue = calculateTotalRevenue(payments, "success");
 * ```
 */
export const calculateTotalRevenue = (
  payments: PaymentDetail[],
  status?: "success" | "failed" | "pending"
): number => {
  const filtered = status
    ? payments.filter((p) => p.status.toLowerCase() === status.toLowerCase())
    : payments;

  return filtered.reduce((sum, p) => sum + p.amount, 0);
};

/**
 * Calculate average ticket value
 *
 * @param payments - Array of payment details
 * @param status - Optional filter by payment status
 * @returns Average ticket value
 *
 * @example
 * ```tsx
 * const avgValue = calculateAverageTicketValue(payments, "success");
 * ```
 */
export const calculateAverageTicketValue = (
  payments: PaymentDetail[],
  status?: "success" | "failed" | "pending"
): number => {
  const filtered = status
    ? payments.filter((p) => p.status.toLowerCase() === status.toLowerCase())
    : payments;

  if (!filtered.length) return 0;

  const total = filtered.reduce((sum, p) => sum + p.amount, 0);
  return total / filtered.length;
};

/**
 * Group payments by vehicle type
 *
 * @param payments - Array of payment details
 * @returns Grouped payments by vehicle type
 *
 * @example
 * ```tsx
 * const grouped = groupPaymentsByVehicleType(payments);
 * console.log(`Car payments: ${grouped.Car.length}`);
 * ```
 */
export const groupPaymentsByVehicleType = (
  payments: PaymentDetail[]
): Record<string, PaymentDetail[]> => {
  return payments.reduce((acc, payment) => {
    const type = payment.vehicleType || "Unknown";
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(payment);
    return acc;
  }, {} as Record<string, PaymentDetail[]>);
};

/**
 * Calculate revenue by vehicle type
 *
 * @param payments - Array of payment details
 * @returns Revenue breakdown by vehicle type
 *
 * @example
 * ```tsx
 * const revenue = calculateRevenueByVehicleType(payments);
 * console.log(`Car revenue: ₹${revenue.Car}`);
 * ```
 */
export const calculateRevenueByVehicleType = (
  payments: PaymentDetail[]
): Record<string, number> => {
  const grouped = groupPaymentsByVehicleType(getSuccessfulPayments(payments));

  return Object.entries(grouped).reduce((acc, [type, typePayments]) => {
    acc[type] = typePayments.reduce((sum, p) => sum + p.amount, 0);
    return acc;
  }, {} as Record<string, number>);
};

/**
 * Format currency value
 *
 * @param amount - Amount to format
 * @param currency - Currency symbol (default: ₹)
 * @returns Formatted currency string
 *
 * @example
 * ```tsx
 * formatCurrency(1234.56) // "₹1,234.56"
 * formatCurrency(1234.56, "$") // "$1,234.56"
 * ```
 */
export const formatCurrency = (amount: number, currency = "₹"): string => {
  return `${currency}${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Format percentage value
 *
 * @param value - Value to format (0-100)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted percentage string
 *
 * @example
 * ```tsx
 * formatPercentage(85.4567) // "85.5%"
 * formatPercentage(85.4567, 2) // "85.46%"
 * ```
 */
export const formatPercentage = (value: number, decimals = 1): string => {
  return `${value.toFixed(decimals)}%`;
};
